/**
 * ============================================================================
 * 문의 작성 페이지
 * ============================================================================
 * 
 * 문의 작성 페이지 컴포넌트
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 기능:
 * - 문의 작성 폼
 * - 첨부파일 선택 및 업로드
 * - 작성 완료 시 티켓 생성
 * 
 * ============================================================================
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ALL_ROUTES } from '../../../core/router/routes';
import SubLayout from '../../components/layout/SubLayout';
import { useFrontAuthStore } from '../../auth/store/authStore';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import { createTicket, uploadFile } from '../../services/inquiryService';
import { TicketCreateRequest, TicketCategory } from '../../types/inquiry';

const Container = styled.div`
  width: 100%;
  max-width: calc(100vw - 260px);
  padding: ${props => props.theme.spacing['3xl']};
  background: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: var(--text-primary, #333);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
`;

const FilterSelect = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.md};
  background: var(--bg-primary, #fff);
  color: var(--text-primary, #333);
  font-size: ${props => props.theme.fontSize.base};
  cursor: pointer;
  height: 44px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.book.color2b};
  }
`;

const FileInput = styled.input`
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.base};
  height: 44px;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.sm};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm};
  background: var(--bg-secondary, #f5f5f5);
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 14px;
`;

const InquiryBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  position: relative;
  margin-top: ${props => props.theme.spacing.xl};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.lg};
`;

const MENU_ITEMS = [
  {
    key: 'profile',
    label: '프로필 수정',
    icon: '/images/book/icon_user.svg',
    children: [
      { key: 'settings-general', label: '기본 정보 설정', path: '/profile' },
      { key: 'settings-school', label: '학적 정보 설정', path: '/profile' },
    ],
  },
  { key: 'inquiry', label: '문의하기', icon: '/images/book/icon_inquiry.svg', path: '/inquiry', active: true },
  { key: 'logout', label: '로그아웃' },
];

const InquiryCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useFrontAuthStore();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<TicketCreateRequest>({
    category: 'QUESTION',
    title: '',
    content: '',
    priority: 'NORMAL',
    file_uuids: [],
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // 티켓 작성
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 제목과 내용이 비어있지 않은지 확인
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    
    // 파일이 있으면 업로드 상태 표시
    if (selectedFiles.length > 0) {
      setUploading(true);
    }

    try {
      // 파일이 있으면 먼저 업로드
      let fileUuids: string[] = [];
      if (selectedFiles.length > 0) {
        try {
          // console.log(`[파일 업로드] ${selectedFiles.length}개 파일 업로드 시작`);
          // 현재 선택된 파일들을 안전하게 참조 (side effect 방지)
          const filesToUpload = [...selectedFiles];
          const uploadPromises = filesToUpload.map(file => uploadFile(file));
          fileUuids = await Promise.all(uploadPromises);
          // console.log(`[파일 업로드] 완료: ${fileUuids.length}개 파일 업로드됨`);
        } catch (error: any) {
          // console.error('파일 업로드 실패:', error);
          const errorMessage = error.response?.data?.error || error.message || '알 수 없는 오류';
          alert(`파일 업로드에 실패했습니다: ${errorMessage}`);
          setLoading(false);
          setUploading(false);
          return;
        } finally {
          setUploading(false);
        }
      }
      
      // 티켓 생성 (업로드된 file_uuid 사용)
      // console.log(`[티켓 생성] 시작 - file_uuids: ${fileUuids.length}개`);
      await createTicket({
        ...formData,
        file_uuids: fileUuids,
      });
      
      // console.log('[티켓 생성] 완료');
      
      // 목록 페이지로 이동
      navigate(ALL_ROUTES.INQUIRY);
    } catch (error: any) {
      // console.error('문의 작성 실패:', error);
      const errorMessage = error.response?.data?.error || error.message || '문의를 작성하는데 실패했습니다.';
      alert(errorMessage);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  // 파일 선택 (업로드는 하지 않고 파일만 저장)
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    // 파일을 선택 목록에 추가 (업로드는 하지 않음)
    setSelectedFiles(prev => [...prev, ...files]);
    
    // 파일 입력 초기화
    if (e.target) {
      e.target.value = '';
    }
  };

  // 파일 삭제
  const handleFileRemove = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMenuClick = useCallback(async (key: string) => {
    if (key === 'logout') {
      try { await logout(); } catch (e) { /* ignore */ }
    }
  }, [logout]);

  return (
    <SubLayout
      menuItems={MENU_ITEMS}
      activeMenu="inquiry"
      onMenuClick={handleMenuClick}
    >
      <Container>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>분류</Label>
            <FilterSelect
              value={formData.category}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setFormData({ ...formData, category: e.target.value as TicketCategory })
              }
            >
              <option value="QUESTION">문의</option>
              <option value="BUG">버그</option>
              <option value="FEATURE">기능</option>
              <option value="ETC">기타</option>
            </FilterSelect>
          </FormGroup>

          <FormGroup>
            <Label>제목</Label>
            <Input
              placeholder="제목을 입력하세요"
              value={formData.title}
              onChange={(value) => setFormData({ ...formData, title: value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>내용</Label>
            <Textarea
              placeholder="내용을 입력하세요"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={15}
            />
          </FormGroup>

          {/* 첨부파일 숨김 처리
          <FormGroup>
            <Label>첨부파일</Label>
            <FileInput
              type="file"
              multiple
              onChange={handleFileSelect}
            />
            {selectedFiles.length > 0 && (
              <FileList>
                {selectedFiles.map((file, idx) => (
                  <FileItem key={idx}>
                    <span>{file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
                    <button
                      type="button"
                      onClick={() => handleFileRemove(idx)}
                      disabled={loading || uploading}
                    >
                      삭제
                    </button>
                  </FileItem>
                ))}
              </FileList>
            )}
            {uploading && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                파일을 업로드하는 중입니다...
              </div>
            )}
          </FormGroup>
          */}

          <ButtonGroup>
            <Button
              type="button"
              size="lg"
              width={180}
              variant="secondary"
              onClick={() => navigate(ALL_ROUTES.INQUIRY)}
            >
              취소
            </Button>
            <Button
              type="submit"
              size="lg"
              width={180}
              disabled={loading || uploading}
            >
              {uploading ? '파일 업로드 중...' : loading ? '작성 중...' : '작성 완료'}
            </Button>
          </ButtonGroup>
        </Form>
      </Container>
      <InquiryBottom>
        <Button
          variant="secondary"
          size="lg"
          width={130}                
          onClick={() => navigate(-1)}
        >
          뒤로가기
        </Button>
      </InquiryBottom>
    </SubLayout>
  );
};

export default InquiryCreatePage;
